import { z } from "zod";
import { pageSchema, StandardResponseSchema, type StandardResponse } from "@/helpers/types";
import { BaseUserSchema } from "@/types";
import { WarehouseSizeMap } from "./components/scheduler/helper";
import { TextFilterMode } from "@/components/tables/types";

/**
 * Enum for warehouse filter API parameter keys
 */
export enum WarehouseFilterApiParamKey {
  COST_FILTER = "costFilter",
  WAREHOUSE_NAME = "warehouseName",
  WAREHOUSE_SIZE = "warehouseSize",
  OPPORTUNITY_FILTER = "opportunityFilter",
  TAGS = "tags",
  SNOWFLAKE_TAGS = "snowflakeTags",
}

export const WarehouseSizeSchema = z.enum(Object.keys(WarehouseSizeMap) as [string, ...string[]]);

export type WarehouseSize = z.infer<typeof WarehouseSizeSchema>;

export const WarehouseConfigSchema = z.object({
  warehouse_size: WarehouseSizeSchema,
});

export type WarehouseConfig = z.infer<typeof WarehouseConfigSchema>;

export const RecurrencePatternSchema = z.object({
  days: z.array(z.string().optional()).optional(),
  end_date: z.string().optional(),
});

export type RecurrencePattern = z.infer<typeof RecurrencePatternSchema>;

export const ScheduleCreateRequestSchema = z.object({
  instance_id: z.string(),
  warehouse_name: z.string(),
  warehouse_configuration: WarehouseConfigSchema,
  start_time: z.string(),
  end_time: z.string(),
  schedule_type: z.string(),
  reason: z.string().nullable().optional(),
  recurrence_pattern: RecurrencePatternSchema.nullable().optional(),
});

export type ScheduleCreateRequest = z.infer<typeof ScheduleCreateRequestSchema>;

export const ScheduleUpdateRequestSchema = z.object({
  warehouse_configuration: WarehouseConfigSchema.optional(),
  start_time: z.string().optional(),
  end_time: z.string().optional(),
  reason: z.string().optional(),
  recurrence_pattern: RecurrencePatternSchema.optional(),
  schedule_type: z.string(),
});

export type ScheduleUpdateRequest = z.infer<typeof ScheduleUpdateRequestSchema>;

export const ScheduleApprovalRequestSchema = z.object({
  reason: z.string().optional(),
});

export type ScheduleApprovalRequest = z.infer<typeof ScheduleApprovalRequestSchema>;

export type ScheduleRejectionRequest = z.infer<typeof ScheduleApprovalRequestSchema>;

export const ScheduleResponseSchema = z.object({
  schedule_id: z.number(),
  warehouse_name: z.string(),
  configuration: z.object({
    cluster: z.string().optional(), // Make cluster optional
    warehouse_size: z.string().optional(), // Make size optional
    tags: z.array(z.string()).default([]), // Default to an empty array
  }),
  start_time: z.string(),
  end_time: z.string(),
  schedule_type: z.string(),
  status: z.string(),
  reason: z.string(),
  reasons: z.array(
    z.object({
      timestamp: z.string(),
      reason: z.string(),
    })
  ),
  created_by: z.number(),
  created_at: z.string(),
  updated_by: z.number().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  recommendation: z
    .object({
      recommendation_id: z.number(),
      applied_schedule_id: z.number(),
      recommendation_data: z.object({}).passthrough(),
      created_at: z.string(),
      metrics: z.object({}).passthrough(),
    })
    .nullable()
    .optional(),
  defaults: z
    .object({
      warehouse_name: z.string(),
      configuration: z.object({}).passthrough(),
      default_schedule: z.object({}).passthrough().nullable().optional(),
    })
    .passthrough()
    .nullable()
    .optional(),

  recurrence_pattern: RecurrencePatternSchema.passthrough().nullable().optional(),
  action: z.string().optional(),
});

export type ScheduleResponse = z.infer<typeof ScheduleResponseSchema>;

export const GetScheduleResponseSchema = z.array(ScheduleResponseSchema);

export type GetScheduleResponse = z.infer<typeof GetScheduleResponseSchema>;

// Warehouse Cost Summary Schema
export const WarehouseCostSummarySchema = z.object({
  realised_savings: z.object({
    value: z.number(),
    display: z.boolean(),
  }),
  estimated_annual_savings: z.object({
    value: z.number(),
    display: z.boolean(),
  }),
  total_opportunities: z.object({
    value: z.number(),
    display: z.boolean(),
  }),
});

export type WarehouseCostSummaryResponse = z.infer<typeof WarehouseCostSummarySchema>;

// Warehouse Summary Cost Graph Schema
export const WarehouseSummaryCostGraphItemSchema = z.object({
  date: z.string(),
  cost: z.number(),
  query_acceleration_cost: z.number(),
  compute_cost: z.number(),
  cloud_services_cost: z.number(),
});

export const WarehouseSummaryCostGraphSchema = z.object({
  warehouse_cost: z.array(WarehouseSummaryCostGraphItemSchema),
});

export type WarehouseSummaryCostGraphItem = z.infer<typeof WarehouseSummaryCostGraphItemSchema>;
export type WarehouseSummaryCostGraphResponse = z.infer<typeof WarehouseSummaryCostGraphSchema>;

export interface TopInfo {
  display: string;
  color?: string;
  value: number;
}

export interface CostGraphEntry {
  time: string;
  date: string;
  cost: number;
  "cost over threshold": number;
  threshold: number;
  "total cost": number;
  "compute cost": number;
  "cloud services cost": number;
  "query acceleration cost": number;
  "cost under threshold": number;
  [key: string]: number | string;
}

// Warehouse Item Schema
export const WarehouseItemSchema = z.object({
  warehouse_rk: z.string(),
  instance_id: z.string(),
  warehouse_name: z.string(),
  warehouse_size: z.string(),
  total_cost: z.string(),
  execution_time_p90: z.string(),
  tags: z.array(z.string()),
  auto_suspend: z.number(),
  median_total_elapsed_time: z.string(),
  median_queuing_time: z.string(),
  median_queuing_time_percentage: z.number(),
  total_active_time: z.string(),
  idle_time: z.string(),
  idle_percentage: z.string(),
  realized_savings: z.string(),
  estimated_annualized_savings: z.string(),
  is_auto_tune_enabled: z.string(),
  auto_resize_mode: z.string(),
  auto_resize_eligible: z.boolean(),
  opportunities: z.array(z.string()),
  snowflake_tags: z.array(z.string()),
});

export type WarehouseItem = z.infer<typeof WarehouseItemSchema>;

// Warehouse List Response Schema using pageSchema
export const WarehouseListResponseSchema = pageSchema(WarehouseItemSchema);

export type WarehouseListResponse = z.infer<typeof WarehouseListResponseSchema>;

// Warehouse Filters Response Schema
export const WarehouseFiltersResponseSchema = z.object({
  warehouse_sizes: z.array(z.string()),
  warehouse_names: z.array(z.string()),
  tags: z.array(z.string()),
  opportunities: z.array(z.string()),
  snowflake_tags: z.array(z.string()),
});

export type WarehouseFiltersResponse = z.infer<typeof WarehouseFiltersResponseSchema>;

// API Parameters Types

// Get Warehouse Summary Cost Graph Parameters
export interface GetWarehouseSummaryCostGraphParams extends GetAllWarehousesParams {
  aggregation?: "day" | "week" | "month";
}

// Get All Warehouses Parameters
export interface GetAllWarehousesParams {
  page?: string;
  size?: string;
  period?: number;
  start_date?: string;
  end_date?: string;
  sortAttribute?: string;
  sortOrder?: string;
  warehouseName?: string | string[];
  search?: string;
  tags?: string | string[];
  opportunityFilter?: string | string[];
  snowflakeTags?: string | string[];
  costFilter?: string | number;
  costFilterMode?: TextFilterMode;
  warehouseSize?: string | string[];
  autoTuneFilter?: string | string[];
  instance_ids?: string | string[];
  resizeEligible?: string;
  resizeMode?: string;
  teams?: string | string[];
  navigationSource?: string;
}

// Get Warehouse Filters Parameters (currently no specific parameters needed)
export interface GetWarehouseFiltersParams {}

// Get Warehouse Details Parameters
export interface GetWarehouseParams {
  period?: number;
}

// Warehouse Details Schema
export const WarehouseDetailsSchema = z.object({
  warehouse_rk: z.string(),
  warehouse_name: z.string(),
  instance_id: z.number(),
  scaling_policy: z.string(),
  min_cluster_count: z.number(),
  max_cluster_count: z.number(),
  owner: z.string(),
  auto_suspend: z.number(),
  auto_resume: z.string(),
  enable_query_acceleration: z.string(),
  query_acceleration_max_scale_factor: z.number(),
  resource_monitor: z.string().nullable(),
  tags: z.array(
    z.object({
      name: z.string(),
      description: z.string(),
    })
  ),
  warehouse_cost: z.unknown().nullable(),
  warehouse_size: z.string(),
  warehouse_type: z.string(),
  threshold: z.any().nullable(),
  comment: z.string().nullable(),
  start_time: z.string().nullable(),
  end_time: z.string().nullable(),
  is_schedule_enabled: z.boolean(),
  default_warehouse_size: z.string(),
});

export type WarehouseDetails = z.infer<typeof WarehouseDetailsSchema>;

// Warehouse Auto Savings Schema
export const WarehouseAutoSavingsGraphItemSchema = z.object({
  date: z.string(),
  spend: z.number(),
  potential: z.number(),
  actual: z.number(),
  min_potential_cost_saved: z.number(),
  max_potential_cost_saved: z.number(),
  min_actual_cost_saved: z.number(),
  max_actual_cost_saved: z.number(),
  min_potential_resize_cost_saved: z.number(),
  max_potential_resize_cost_saved: z.number(),
  min_actual_resize_cost_saved: z.number(),
  max_actual_resize_cost_saved: z.number(),
  warehouse_resize_potential: z.number(),
  warehouse_resize_actual: z.number(),
  min_total_potential_cost_saved: z.number(),
  max_total_potential_cost_saved: z.number(),
  min_total_actual_cost_saved: z.number(),
  max_total_actual_cost_saved: z.number(),
});

export const WarehouseAutoSavingsTopInfoItemSchema = z.object({
  value: z.union([z.string(), z.number()]),
  max_value: z.union([z.string(), z.number()]).optional(),
  min_value: z.union([z.string(), z.number()]).optional(),
  color: z.string().optional(),
  display: z.boolean().optional(),
});

export type WarehouseAutoSavingsGraphItem = z.infer<typeof WarehouseAutoSavingsGraphItemSchema>;

export const WarehouseAutoSavingsResponseSchema = z.object({
  graph_data: z.array(WarehouseAutoSavingsGraphItemSchema),
  top_info: z.object({
    "Realized Savings": WarehouseAutoSavingsTopInfoItemSchema.optional(),
    realized_saving: WarehouseAutoSavingsTopInfoItemSchema.optional(),
    estimated_annual_savings: WarehouseAutoSavingsTopInfoItemSchema.optional(),
  }),
});

export type WarehouseAutoSavingsResponse = z.infer<typeof WarehouseAutoSavingsResponseSchema>;

// Get Warehouse Auto Savings Parameters
export interface GetWarehouseAutoSavingsParams {
  start_date?: string;
  end_date?: string;
  aggregation?: string;
}

// Warehouse Auto Tune Configuration Schema
export const WarehouseAutoTuneConfigurationSchema = z
  .object({
    auto_tune_configuration: z.object({
      auto_tune_enabled: z.boolean(),
    }),
    resizing_configuration: z.object({
      resizing_enabled: z.boolean(),
      resizing_configuration: z
        .object({
          mode: z.enum(["disabled", "auto_resize", "manual_resize"]),
          backoff_configuration: z.object({
            max_latency_percentage: z.number(),
            max_queue_time_percentage: z.number(),
          }),
          auto_approve_resizing: z.boolean().optional(),
        })
        .optional(),
    }),
    dynamic_resizing_eligible: z.boolean().nullable(),
    dynamic_resizing_eligible_reason: z.string().nullable(),
  })
  .passthrough();

export type WarehouseAutoTuneConfiguration = z.infer<typeof WarehouseAutoTuneConfigurationSchema>;

// Set Warehouse Auto Tune Configuration Request Schema
export const SetWarehouseAutoTuneConfigurationRequestSchema = z.object({
  auto_tune_configuration: z
    .object({
      auto_tune_enabled: z.boolean(),
    })
    .optional(),
  resizing_configuration: z
    .object({
      resizing_enabled: z.boolean(),
      resizing_configuration: z
        .object({
          mode: z.enum(["disabled", "auto_resize", "manual_resize"]),
          backoff_configuration: z
            .object({
              max_latency_percentage: z.number(),
              max_queue_time_percentage: z.number(),
            })
            .optional(),
          auto_approve_resizing: z.boolean().optional(),
        })
        .optional(),
    })
    .optional(),
  dynamic_resizing_eligible: z.boolean().nullable().optional(),
  dynamic_resizing_eligible_reason: z.string().nullable().optional(),
});

export type SetWarehouseAutoTuneConfigurationRequest = z.infer<
  typeof SetWarehouseAutoTuneConfigurationRequestSchema
>;

// Toggle Auto Tune Response Schema (flat response from toggle endpoint)
export const WarehouseAutoTuneToggleResponseSchema = z
  .object({
    auto_tune_enabled: z.boolean(),
    resizing_enabled: z.boolean(),
    resizing_mode: z.string().nullable(),
  })
  .passthrough();

export type WarehouseAutoTuneToggleResponse = z.infer<typeof WarehouseAutoTuneToggleResponseSchema>;

// Warehouse Decision Data Response Schema
export const WarehouseDecisionDataResponseSchema = z.object({
  decision_chart: z.array(
    z.object({
      date: z.string(),
      decisions: z.coerce.number(),
    })
  ),
});

export type WarehouseDecisionDataResponse = z.infer<typeof WarehouseDecisionDataResponseSchema>;

// Get Warehouse Decision Data Params Schema
export const GetWarehouseDecisionDataParamsSchema = z.object({
  period: z.number().optional(),
});

export type GetWarehouseDecisionDataParams = z.infer<typeof GetWarehouseDecisionDataParamsSchema>;

// Warehouse Query Execution Times Response Schema
export const WarehouseQueryExecutionTimesResponseSchema = z.object({
  query_execution_time: z.array(
    z.object({
      date: z.string(),
      execution_time: z.number(),
      p90_execution_time: z.number(),
      bytes_scanned: z.number(),
      avg_queued_overload_time: z.number(),
      p90_queued_overload_time: z.number(),
      query_count: z.number(),
      execution_time_ui: z.string(),
      p90_execution_time_ui: z.string(),
      bytes_scanned_ui: z.string(),
      avg_queued_overload_time_ui: z.string(),
      p90_queued_overload_time_ui: z.string(),
    })
  ),
});

export type WarehouseQueryExecutionTimesResponse = z.infer<
  typeof WarehouseQueryExecutionTimesResponseSchema
>;

// Get Warehouse Query Execution Times Params Schema
export const GetWarehouseQueryExecutionTimesParamsSchema = z.object({
  period: z.number().optional(),
});

export type GetWarehouseQueryExecutionTimesParams = z.infer<
  typeof GetWarehouseQueryExecutionTimesParamsSchema
>;

// Warehouse Audit Event User Schema - extends BaseUserSchema with additional fields
export const AuditEventUserSchema = BaseUserSchema.extend({
  phone: z.string().nullable(),
  display_name: z.string().nullable(),
  role_title: z.string().nullable(), // Override to make nullable
  attributes: z.unknown().nullable(), // Override to make nullable
});

export type AuditEventUser = z.infer<typeof AuditEventUserSchema>;

// Warehouse Audit Event Schema
export const WarehouseAuditEventSchema = z.object({
  rk: z.string(),
  warehouse_name: z.string(),
  changed_by: z.number(),
  event_type: z.string(),
  change_timestamp: z.string(),
  attributes: z.any().nullable(),
  user: AuditEventUserSchema,
  event_message: z.string(),
  reason: z.string().nullable(),
});

export type WarehouseAuditEvent = z.infer<typeof WarehouseAuditEventSchema>;

// Warehouse Audit Events Response Schema
export const WarehouseAuditEventsResponseSchema = pageSchema(WarehouseAuditEventSchema);

export type WarehouseAuditEventsResponse = z.infer<typeof WarehouseAuditEventsResponseSchema>;

// Get Warehouse Audit Events Params Schema
export const GetWarehouseAuditEventsParamsSchema = z.object({
  page: z.number().optional(),
  size: z.number().optional(),
  period: z.number().optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  event_types: z.array(z.string()).optional(),
});

export type GetWarehouseAuditEventsParams = z.infer<typeof GetWarehouseAuditEventsParamsSchema>;

// Get Warehouse Cost Params Schema
export const GetWarehouseCostParamsSchema = z.object({
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  aggregation: z.string().optional(),
  period: z.number().optional(),
});

export type GetWarehouseCostParams = z.infer<typeof GetWarehouseCostParamsSchema>;

// Warehouse Cost Response Schema
export const WarehouseCostResponseSchema = z.object({
  warehouse_cost: z.array(
    z.object({
      date: z.string(),
      cost: z.number(),
      query_acceleration_cost: z.number(),
      compute_cost: z.number(),
      cloud_services_cost: z.number(),
    })
  ),
  utilization_efficiency: z
    .array(
      z.object({
        date: z.string(),
        utilization: z.number(),
      })
    )
    .optional()
    .default([]),
  threshold: z.number().nullable(),
  start_time: z.string().optional(),
  end_time: z.string().optional(),
});

export type WarehouseCostResponse = z.infer<typeof WarehouseCostResponseSchema>;

// Warehouse Cluster Utilization Response Schema
export const WarehouseClusterUtilizationResponseSchema = z.object({
  overall_utilization_efficiency: z.array(
    z.object({
      date: z.string(),
      utilization: z.number(),
    })
  ),
  cluster_utilization_efficiency: z.record(
    z.array(
      z.object({
        date: z.string(),
        utilization: z.number(),
      })
    )
  ),
  start_time: z.string().optional(),
  end_time: z.string().optional(),
});

export type WarehouseClusterUtilizationResponse = z.infer<
  typeof WarehouseClusterUtilizationResponseSchema
>;

// Warehouse Last Event Time Response Schema
export const WarehouseLastEventTimeResponseSchema = z.object({
  timestamp: z.string(),
  time_string: z.string(),
});

export type WarehouseLastEventTimeResponse = z.infer<typeof WarehouseLastEventTimeResponseSchema>;

// Warehouse Metadata Item Schema
export const WarehouseMetadataItemSchema = z.object({
  warehouse_name: z.string(),
  rk: z.string(),
});

export const WarehouseMetadataResponseSchema = z.array(WarehouseMetadataItemSchema);

export type WarehouseMetadataItem = z.infer<typeof WarehouseMetadataItemSchema>;
export type WarehouseMetadataResponse = z.infer<typeof WarehouseMetadataResponseSchema>;

// Warehouse Insights Request Schema
export const WarehouseInsightsRequestSchema = z.object({
  rks: z.array(z.string()),
});

export type WarehouseInsightsRequest = z.infer<typeof WarehouseInsightsRequestSchema>;

// Warehouse Insights Response Schema
export const WarehouseInsightsItemSchema = z.object({
  warehouse_rk: z.string(),
  tag_rk: z.string(),
});

export const WarehouseInsightsResponseSchema = z.array(WarehouseInsightsItemSchema);

export type WarehouseInsightsItem = z.infer<typeof WarehouseInsightsItemSchema>;
export type WarehouseInsightsResponse = z.infer<typeof WarehouseInsightsResponseSchema>;

// Toggle Warehouse Schedules Response Schema
export const ToggleWarehouseSchedulesResponseSchema = StandardResponseSchema;
export type ToggleWarehouseSchedulesResponse = StandardResponse;

// Toggle Warehouse Auto Savings Response Schema
export const ToggleWarehouseAutoSavingsResponseSchema = StandardResponseSchema;
export type ToggleWarehouseAutoSavingsResponse = StandardResponse;

// Warehouse Auto Savings Configuration Response Schema
export const WarehouseAutoSavingsConfigurationResponseSchema = z.object({
  is_auto_tune_enabled: z.boolean().nullable().optional(),
  savings_level: z.string().nullable().optional(),
});

export type WarehouseAutoSavingsConfigurationResponse = z.infer<
  typeof WarehouseAutoSavingsConfigurationResponseSchema
>;

// Get Warehouse Scheduler Events Parameters Schema
export const GetWarehouseSchedulerEventsParamsSchema = z.object({
  instance_id: z.string(),
  warehouse_name: z.string().optional(),
  start_time: z.string().optional(),
  end_time: z.string().optional(),
  schedule_type: z.string().optional(),
  filter_type: z.string().optional(),
  combine_consecutive: z.boolean().optional(),
});

export interface GetWarehouseSchedulerEventsParams extends z.infer<
  typeof GetWarehouseSchedulerEventsParamsSchema
> {}
